import { skipToken } from "@reduxjs/toolkit/query";
import axios from "axios";
import moment from "moment";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Alert,
} from "react-bootstrap";

import { AUTH_ROLES } from "api/common/types/auth.types";
import { useGetFileMutation } from "api/memberPortal/files/files.api";
import { useAddNoteMutation } from "api/memberPortal/notes/notes.api";
import {
  useGetMaintenanceQuery,
  useMaintenanceUpdateAsFormDataMutation,
  useMaintenanceCreateAsFormDataMutation,
} from "api/memberPortal/project/maintenance/maintenance.api";
import { MAINTENANCE_STATUSES } from "api/memberPortal/project/maintenance/maintenance.types";
import { useProductUpdateMutation } from "api/memberPortal/project/product/product.api";
import {
  PRODUCT_STATUSES,
  PRODUCT_SUBMISSION_TYPES,
} from "api/memberPortal/project/product/productStatuses.type";
import { useGetAllProjectPpByProductIdQuery } from "api/memberPortal/project/protectionProfiles/protectionProfiles.api";
import HTMLEditor from "components/UI/HTMLEditor";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import { useAuth } from "hooks/useAuth";
import { formatFormBodyDate, getISOCurrentDate } from "utils/timeUtils";

const MAINTENANCE = {};
const PROJPPS = [];

export default function RequestAssuranceForm({
  show,
  handleCloseModal,
  product,
  refetch,
  setAlertInfo,
  fromProductDetails,
}) {
  const [editedFileLabel, setEditedFileLabel] = useState("");
  const [formInfo, setFormInfo] = useState({});
  const [enforceFocusModal, setEnforceFocusModal] = useState(true);
  const [alert, setAlert] = useState("");
  const [files, setFiles] = useState([]);

  const { currentUser, permissions, authToken, csrfToken } = useAuth();
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  const saveDraft = useRef(false);

  const isNiap = permissions.role_type === AUTH_ROLES.NIAP;

  const { data: maintenance = MAINTENANCE, isLoading: isLoadingMaintenance } =
    useGetMaintenanceQuery(
      product?.current_assurance_maintenance?.maintenance_id ?? skipToken,
    );

  const getFiles = useCallback(() => {
    if (maintenance?.maintenance_id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}file/get_files_by_type_and_file_source_type_and_type_id/?file_type=assurance-maintenance&file_source_type=Assurance+Maintenance&file_type_id=${maintenance?.maintenance_id}`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          setFiles(response.data);
        })
        .catch(() => setFiles([]));
    }
  }, [maintenance?.maintenance_id]);

  useEffect(() => {
    getFiles();
  }, [getFiles]);

  const { data: projpps = PROJPPS, isLoading: isLoadingProjPps } =
    useGetAllProjectPpByProductIdQuery(product.product_id ?? skipToken);

  const [triggerGetFile] = useGetFileMutation();
  const [triggerAddNote] = useAddNoteMutation();
  const [triggerUpdateProduct] = useProductUpdateMutation();
  const [triggerUpdateMaintenance] = useMaintenanceUpdateAsFormDataMutation();
  const [triggerCreateMaintenance] = useMaintenanceCreateAsFormDataMutation();

  useEffect(() => {
    if (show && (isLoadingMaintenance || isLoadingProjPps)) {
      showLoadingSpinner("Loading Request Assurance Maintenance Form...");
    } else {
      hideLoadingSpinner();
    }
  }, [
    show,
    isLoadingMaintenance,
    isLoadingProjPps,
    showLoadingSpinner,
    hideLoadingSpinner,
  ]);

  useEffect(() => {
    if (maintenance?.maintenance_id) {
      setFormInfo({
        ...formInfo,
        vendor_poc: maintenance?.vendor_poc,
        vendor_email: maintenance?.vendor_email,
        vendor_phone: maintenance?.vendor_phone,
      });
    } else {
      setFormInfo({
        ...formInfo,
        vendor_poc: product.vendor_poc,
        vendor_email: product.vendor_email,
        vendor_phone: product.vendor_phone,
      });
    }
  }, [product.product_id, maintenance?.maintenance_id]);

  if (isLoadingMaintenance || isLoadingProjPps) {
    return null;
  }

  const createNote = (maintenance_id) => {
    if (formInfo?.maintenance_release) {
      try {
        triggerAddNote({
          title: "Notes on Changes",
          note: formInfo.maintenance_release,
          internal_only: true,
          note_type: "assurance_maintenance",
          note_type_id: maintenance_id,
          visible_to_validators: true,
          visible_to_labs: false,
          visible_to_smes: false,
          submitted_on: getISOCurrentDate(),
        }).unwrap();
      } catch (error) {
        console.log("Error Creating Note: ", error);
      }
    }
  };

  const handleChange = (e) => {
    if (e.target.name === "file") {
      const newFiles = files ? files : [];
      newFiles.push({
        file_display_name: formInfo.file_display_name,
        uploaded_on: getISOCurrentDate(),
        file_name: e.target.files[0].name,
        file_label: formInfo.file_label,
        file: e.target.files[0],
        active: true,
        private: formInfo.file_public_status === "Public" ? false : true,
      });
      setFiles([...newFiles]);
    }
    if (e.target.name === "file_display_name") {
      setEditedFileLabel(e.target.value);
      if (e.target.value === "Impact Analysis Report (IAR)") {
        setFormInfo({
          ...formInfo,
          [e.target.name]: e.target.value,
          file_label: e.target.value,
          file_public_status: "Proprietary",
        });
      } else {
        setFormInfo({
          ...formInfo,
          [e.target.name]: e.target.value,
          file_label: e.target.value,
        });
      }
    } else if (e.target.name === "file_label") {
      setEditedFileLabel(e.target.value);
      setFormInfo({ ...formInfo, [e.target.name]: e.target.value });
    } else {
      setFormInfo({ ...formInfo, [e.target.name]: e.target.value });
    }
  };

  const removeFile = async (file, idx) => {
    if (file.file_id) {
      await axios
        .put(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}file/${file.file_id}/`,
          {
            active: false,
          },
          {
            withCredentials: true,
            headers: {
              "X-CSRFToken": csrfToken,
              Authorization: `Token ${authToken}`,
            },
          },
        )
        .then(() => getFiles())
        .catch((error) => console.log("Unable to update file", error));
    } else {
      const newFiles = files.slice();
      newFiles.splice(idx, 1);
      setFiles(newFiles);
    }
  };

  const missingRequiredFiles = () => {
    //check if ST and IAR are included if not draft
    let missingFiles = false;
    if (!saveDraft.draft) {
      if (
        !files?.some((file) => file.file_display_name === "Security Target")
      ) {
        missingFiles = true;
      }
      if (
        !files?.some(
          (file) => file.file_display_name === "Impact Analysis Report (IAR)",
        )
      ) {
        missingFiles = true;
      }
      if (missingFiles) {
        setAlert(
          "Please include a Security Target file and an IAR file before submitting form.",
        );
      } else {
        setAlert("");
      }
    }
    return missingFiles;
  };

  const createSubmittedData = () => {
    const submittedData = new FormData();
    submittedData.append("vendor_poc", formInfo?.vendor_poc || "");
    submittedData.append("vendor_email", formInfo?.vendor_email || "");
    submittedData.append("vendor_phone", formInfo?.vendor_phone || "");
    submittedData.append("team_due_date", formInfo?.team_due_date || "");
    submittedData.append(
      "maintenance_release",
      formInfo?.maintenance_release || "",
    );
    submittedData.append(
      "rejection_rationale",
      maintenance?.rejection_rationale || "",
    );
    submittedData.append("estimated_date", formInfo?.estimated_date || "");
    submittedData.append("title", formInfo?.title || product.product_name);
    submittedData.append("iar_received", getISOCurrentDate());
    submittedData.append(
      "estimated_date",
      formatFormBodyDate(formInfo?.estimated_date) || "",
    );
    if (isNiap) {
      //for Niap (vendor) view
      submittedData.append("vendor_request", currentUser.id);
      submittedData.append("vendor_request_date", getISOCurrentDate());
      submittedData.append("status", MAINTENANCE_STATUSES.RELEASED);
    } else {
      //for lab view
      submittedData.append("lab_request", currentUser.id);
      submittedData.append("lab_request_date", getISOCurrentDate());
      submittedData.append(
        "status",
        saveDraft.draft
          ? MAINTENANCE_STATUSES.DRAFT
          : MAINTENANCE_STATUSES.REQUESTED,
      );
    }

    if (!maintenance?.maintenance_id) {
      submittedData.append("creator", currentUser.id);
      submittedData.append("editor", currentUser.id);
      submittedData.append("entrydate", getISOCurrentDate());
      submittedData.append("product", product?.product_id);
    } else {
      submittedData.append("editor", currentUser.id);
      submittedData.append("moddate", getISOCurrentDate());
    }

    if (files.length > 0) {
      for (let f in files) {
        if (!files[f].file_mime_type) {
          // checks if files has aready been added
          submittedData.append("attachments_file", files[f].file);
          submittedData.append(
            "attachments_source_type",
            "Assurance Maintenance",
          );
          submittedData.append("active", JSON.stringify(true));
          submittedData.append("attachments_type", files[f].file_display_name);
          submittedData.append("file_label", files[f].file_label);
          submittedData.append("file_name", files[f].file_name);
          submittedData.append("private", files[f].private);
        }
      }
    }

    return submittedData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (!saveDraft.draft && (!form.checkValidity() || missingRequiredFiles())) {
      e.stopPropagation();
      return;
    }

    const submittedData = createSubmittedData();

    const isUpdate = maintenance?.maintenance_id;
    const actionType = isUpdate ? "Updating" : "Creating";

    try {
      showLoadingSpinner(`${actionType} Assurance Maintenance...`);

      // Handle maintenance creation/update
      let response;
      if (isUpdate) {
        // For update, we need to add the maintenance_id to the FormData
        submittedData.append("maintenance_id", maintenance.maintenance_id);
        response = await triggerUpdateMaintenance(submittedData).unwrap();
      } else {
        response = await triggerCreateMaintenance(submittedData).unwrap();

        await triggerUpdateProduct({
          product_id: product.product_id,
          current_assurance_maintenance: response.maintenance_id,
          submission_type: PRODUCT_SUBMISSION_TYPES.ASSURANCE_MAINTENANCE,
          status: !saveDraft.draft
            ? PRODUCT_STATUSES.IN_ASSURANCE_MAINTENANCE
            : PRODUCT_STATUSES.CERTIFIED,
          assigned_lab: product?.assigned_lab?.org_id,
        }).unwrap();
      }

      // Send alert and mail only if not draft
      if (!saveDraft.draft) {
        createNote(response.maintenance_id);
      }

      // Handle success
      refetch();
      handleCloseModal();

      if (!fromProductDetails) {
        const message = saveDraft.draft
          ? "Assurance Maintenance Draft Saved!"
          : "Assurance Maintenance Successfully Submitted!";

        setAlertInfo({
          message,
          type: "success",
        });
      }
    } catch (error) {
      console.log(`Error ${actionType} Assurance Maintenance: `, error);
      setAlert(`Error ${actionType} Assurance Maintenance. Please try again.`);
    } finally {
      hideLoadingSpinner();
    }
  };

  const downloadFile = async (fileId) => {
    showLoadingSpinner("Downloading");

    await triggerGetFile(fileId);

    hideLoadingSpinner();
  };

  // AM request/release notifications are sent by the backend when the
  // maintenance status transitions to "Requested" (lab/vendor "Request AM") or
  // "Released" (NIAP "Release AM") in ProjectMaintenanceViewSet. See
  // notify_project_am_requested / notify_project_am_request_released.

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      enforceFocus={enforceFocusModal}
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            +ASSURANCE MAINTENANCE {!isNiap && "REQUEST"}:{" "}
            <span className="text-primary">
              VID{" "}
              {maintenance?.product?.v_id
                ? maintenance.product.v_id
                : product.v_id}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row className="mt-3">
              <Col>
                <Row>
                  <Form.Group className="mb-3" controlId="product_name">
                    <Row>
                      <Col sm={4}>
                        <Form.Label column>Product Name:</Form.Label>
                      </Col>
                      <Col>
                        <Form.Control
                          plaintext
                          name="product_name"
                          defaultValue={product?.product_name}
                          readOnly
                          hidden
                        />
                        {product?.product_name}
                      </Col>
                    </Row>
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group className="mb-3" controlId="certification_date">
                    <Row>
                      <Col sm={4}>
                        <Form.Label>Certification Date:</Form.Label>
                      </Col>
                      <Col className="m-0">
                        <Form.Control
                          plaintext
                          className="p-0"
                          defaultValue={
                            product?.certification_date &&
                            moment
                              .utc(product?.certification_date)
                              .format("MM/DD/YYYY")
                          }
                          readOnly
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group className="mb-3" controlId="expiration_date">
                    <Row>
                      <Col sm={4}>
                        <Form.Label>Expiration Date:</Form.Label>
                      </Col>
                      <Col>
                        <Form.Control
                          plaintext
                          className="p-0"
                          defaultValue={
                            product?.assurance_maintenance_date &&
                            moment
                              .utc(product?.assurance_maintenance_date)
                              .format("MM/DD/YYYY")
                          }
                          readOnly
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group className="mb-3" controlId="pp_claim">
                    <Row>
                      <Col sm={4}>
                        <Form.Label>Protection Profile Claims:</Form.Label>
                      </Col>
                      <Col>
                        {projpps?.map((pp, idx) => (
                          <OverlayTrigger
                            key={pp.pp_id}
                            placement="top"
                            overlay={<Tooltip>{pp.pp__pp_name}</Tooltip>}
                          >
                            <div key={idx} style={{ width: "max-content" }}>
                              {pp.pp__pp_short_name}
                            </div>
                          </OverlayTrigger>
                        ))}
                      </Col>
                    </Row>
                  </Form.Group>
                </Row>
              </Col>
            </Row>
            {maintenance && maintenance?.rejection_rationale && (
              <Row>
                <Col>
                  <h6 className="fw-bold form-title mb-2">
                    RATIONALE FOR REJECTION
                  </h6>
                  <Form.Control
                    as="textarea"
                    name="rejection_rationale"
                    rows={4}
                    disabled={true}
                    onChange={handleChange}
                    defaultValue={maintenance.rejection_rationale}
                  />
                </Col>
              </Row>
            )}

            {isNiap && (
              <Row className="mb-3">
                <Form.Group controlId="title">
                  <Form.Label>Maintenance Release:</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    defaultValue={product?.product_name}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Row>
            )}
            <Row className="mt-3">
              <Col>
                <p className="fw-bold form-title mb-2">
                  UPDATE POC INFORMATION
                </p>
              </Col>
            </Row>
            <Row className="mt-1">
              <Col>
                <Form.Group controlId="vendor_poc">
                  <Form.Label>POC Name: </Form.Label>
                  <Form.Control
                    type="text"
                    name="vendor_poc"
                    onChange={handleChange}
                    value={formInfo.vendor_poc}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <Form.Group controlId="vendor_email">
                  <Form.Label>POC Email:</Form.Label>
                  <Form.Control
                    type="text"
                    name="vendor_email"
                    onChange={handleChange}
                    value={formInfo.vendor_email}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <Form.Group controlId="vendor_phone">
                  <Form.Label>POC Phone Number:</Form.Label>
                  <Form.Control
                    type="text"
                    name="vendor_phone"
                    onChange={handleChange}
                    value={formInfo.vendor_phone}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-5">
              <Col>
                <h6 className="fw-bold form-title mb-2">ATTACH FILES</h6>
                <Row>
                  <Col>
                    <Form.Select
                      data-cy="file_display_name"
                      name="file_display_name"
                      onChange={handleChange}
                    >
                      <option value="">Type</option>
                      <option value="Security Target">Security Target</option>
                      <option value="Administrative Guide (AGD)">
                        Admin Guide
                      </option>
                      <option value="Impact Analysis Report (IAR)">IAR</option>
                      <option value="Other Documents">Other Documents</option>
                    </Form.Select>
                  </Col>
                  <Col>
                    <Form.Control
                      type="text"
                      name="file_label"
                      placeholder="Label"
                      value={editedFileLabel}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col>
                    <Form.Select
                      data-cy="file_status"
                      name="file_public_status"
                      value={formInfo.file_public_status}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="Public">Public</option>
                      <option value="Proprietary">Proprietary</option>
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col sm={6}>
                    <Form.Group controlId="formFileMultiple">
                      <Form.Control
                        type="file"
                        name="file"
                        onChange={handleChange}
                        disabled={
                          !formInfo.file_label ||
                          !formInfo.file_display_name ||
                          !formInfo.file_public_status
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="mt-2">
              <p className="fst-italic text-secondary">
                Security Target and IAR Files Required to Submit
              </p>
            </Row>
            <Row className="mt-3">
              <Col lg={11}>
                {files?.length > 0 && (
                  <div className="border">
                    <p className="mx-3 mt-2">Attached Files</p>
                    {files
                      ?.filter((file) => {
                        return (
                          new Date().getFullYear() ===
                          new Date(file?.uploaded_on).getFullYear()
                        );
                      })
                      ?.map((file, idx) => (
                        <Row
                          className="m-3 d-flex justify-content-around"
                          key={file.file_id}
                        >
                          <Col sm={3}>
                            <Button
                              variant="link"
                              className="text-bright-navy text-decoration-underline p-0"
                              onClick={() => downloadFile(file.file_id)}
                            >
                              {file.file_name}
                            </Button>
                          </Col>
                          <Col sm={3}>{file.file_label}</Col>
                          <Col sm={4}>
                            {file.private ? "Proprietary" : "Public"}
                          </Col>
                          <Col sm={1} className="d-flex justify-content-center">
                            <Button
                              variant="outline-primary"
                              className="attachment-remove"
                              onClick={() => removeFile(file, idx)}
                            >
                              X
                            </Button>
                          </Col>
                        </Row>
                      ))}
                  </div>
                )}
              </Col>
            </Row>
            {isNiap && (
              <Row>
                <Form.Group controlId="team_due_date">
                  <Form.Label>Validation Team Assignment Due Date:</Form.Label>
                  <Form.Control
                    className="mb-3 w-50"
                    type="date"
                    name="team_due_date"
                    onChange={handleChange}
                  />
                </Form.Group>
              </Row>
            )}
            <Row className="mt-3">
              <Col>
                <h6 className="fw-bold form-title mb-2">NOTES</h6>
                <HTMLEditor
                  name="maintenance_release"
                  handleChange={handleChange}
                  setIsValid={() => {}}
                  isValid={true}
                  defaultValue={maintenance?.maintenance_release}
                  setEnforceFocusModal={setEnforceFocusModal}
                />
              </Col>
            </Row>
            {alert !== "" && (
              <Row className="m-3 mb-0">
                <Alert
                  className="mb-0"
                  variant="danger"
                  dismissible
                  onClose={() => setAlert("")}
                >
                  {alert}
                </Alert>
              </Row>
            )}
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleCloseModal}
            className="mx-4"
          >
            Cancel
          </Button>
          {!isNiap && (
            <Button
              type="submit"
              variant="warning"
              onClick={() => {
                saveDraft.draft = true;
              }}
            >
              {maintenance?.maintenance_id ? "Update Draft" : "Save as Draft"}
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            className="mx-4"
            onClick={() => {
              saveDraft.draft = false;
            }}
          >
            {isNiap ? "Release AM" : "Request AM"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
